import React, { useState, useEffect } from 'react';
import { apiService } from '../services/apiService';

const HiringManagersForm = ({ userInfo }) => {
    // Existing hiring managers from backend
    const [existingHiringManagers, setExistingHiringManagers] = useState([]);
    const [fetchLoading, setFetchLoading] = useState(true);
    const [fetchError, setFetchError] = useState('');
    const [hasInitialLoaded, setHasInitialLoaded] = useState(false);

    // New hiring managers to add
    const [newHiringManagers, setNewHiringManagers] = useState([{ name: '', email: '' }]);
    const [addLoading, setAddLoading] = useState(false);
    const [addError, setAddError] = useState('');
    const [addSuccess, setAddSuccess] = useState('');

    // Delete operation states
    const [deleteError, setDeleteError] = useState('');

    // Bulk selection states
    const [selectedManagers, setSelectedManagers] = useState(new Set());
    const [isSelectAll, setIsSelectAll] = useState(false);
    const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);

    // Fetch existing hiring managers on component mount
    useEffect(() => {
        fetchHiringManagers();
    }, [userInfo.tenantId]);

    const fetchHiringManagers = async () => {
        setFetchLoading(true);
        setFetchError('');

        try {
            const organizationId = userInfo?.tenantId ;
            const response = await apiService.getHiringManagers(organizationId);

            // Handle the response structure: response.data[0].emails
            if (response && response.success && response.data && response.data.length > 0 && response.data[0].emails) {
                const hiringManagers = response.data[0].emails.map((manager, index) => ({
                    id: index, // Using index as ID since no ID is provided in response
                    name: manager.Name,
                    email: manager.Email
                }));
                setExistingHiringManagers(hiringManagers);
            } else {
                setExistingHiringManagers([]);
            }
        } catch (error) {
            console.log('Error fetching hiring managers:', error);
            setFetchError('Failed to fetch hiring managers');
            setExistingHiringManagers([]);
        }

        // Add a small delay to ensure smooth UI transition
        setTimeout(() => {
            setFetchLoading(false);
            setHasInitialLoaded(true);
        }, 3000);
    };

    // Bulk selection functions
    const handleSelectManager = (managerEmail) => {
        const newSelected = new Set(selectedManagers);
        if (newSelected.has(managerEmail)) {
            newSelected.delete(managerEmail);
        } else {
            newSelected.add(managerEmail);
        }
        setSelectedManagers(newSelected);
        
        // Update select all state
        setIsSelectAll(newSelected.size === existingHiringManagers.length && existingHiringManagers.length > 0);
    };

    const handleSelectAll = () => {
        if (isSelectAll) {
            setSelectedManagers(new Set());
            setIsSelectAll(false);
        } else {
            const allEmails = new Set(existingHiringManagers.map(manager => manager.email));
            setSelectedManagers(allEmails);
            setIsSelectAll(true);
        }
    };

    // Bulk delete function
    const bulkDeleteManagers = async () => {
        if (selectedManagers.size === 0) {
            setDeleteError('Please select hiring managers to delete');
            return;
        }

        setBulkDeleteLoading(true);
        setDeleteError('');

        try {
            const organizationId = userInfo?.tenantId;
            const emailsToDelete = Array.from(selectedManagers);
            
            // Call API to delete multiple managers
            await apiService.bulkDeleteHiringManagers(emailsToDelete, organizationId);

            // Remove deleted managers from local state
            setExistingHiringManagers(prev =>
                prev.filter(manager => !selectedManagers.has(manager.email))
            );

            // Clear selection
            setSelectedManagers(new Set());
            setIsSelectAll(false);

            console.log(`Successfully deleted ${emailsToDelete.length} hiring managers`);
        } catch (error) {
            console.log('Error bulk deleting hiring managers:', error);
            setDeleteError('Failed to delete selected hiring managers');
        }

        setBulkDeleteLoading(false);
    };

    // Add new hiring manager input field
    const addNewHiringManagerField = () => {
        setNewHiringManagers([...newHiringManagers, { name: '', email: '' }]);
    };

    // Remove new hiring manager input field
    const removeNewHiringManagerField = (index) => {
        const updatedManagers = newHiringManagers.filter((_, i) => i !== index);
        setNewHiringManagers(updatedManagers);
    };

    // Update new hiring manager field (name or email)
    const updateNewHiringManager = (index, field, value) => {
        const updatedManagers = [...newHiringManagers];
        updatedManagers[index][field] = value;
        setNewHiringManagers(updatedManagers);
    };

    // Save new hiring managers
    const saveNewHiringManagers = async (e) => {
        e.preventDefault();
        setAddLoading(true);
        setAddError('');
        setAddSuccess('');

        // Filter out empty hiring managers
        const validHiringManagers = newHiringManagers.filter(
            manager => manager.name.trim() !== '' && manager.email.trim() !== ''
        );

        if (validHiringManagers.length === 0) {
            setAddError('At least one hiring manager with name and email is required');
            setAddLoading(false);
            return;
        }

        // Validate email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const invalidEmails = validHiringManagers.filter(manager => !emailRegex.test(manager.email));

        if (invalidEmails.length > 0) {
            setAddError('Please enter valid email addresses for all hiring managers');
            setAddLoading(false);
            // Show alert for invalid emails
            alert(`Invalid email format detected. Please check all email addresses.`);
            return;
        }

        try {
            const organizationId = userInfo?.tenantId;
            const hiringManagersData = validHiringManagers.map(manager => ({
                name: manager.name.trim(),
                email: manager.email.trim(),
                organization_id: organizationId
            }));

            await apiService.saveHiringManagers(hiringManagersData);
            setAddSuccess(`${validHiringManagers.length} hiring manager(s) added successfully!`);

            // Reset form and refresh the list
            setNewHiringManagers([{ name: '', email: '' }]);
            fetchHiringManagers();

        } catch (error) {
            console.log('Error saving hiring managers:', error);
            setAddError(error.message);
        }

        setAddLoading(false);
    };

    return (
        <div className="form-section">
            {/* Add New Hiring Managers Section */}
            <div className="teams-card" style={{ marginBottom: '20px' }}>
                <div className="teams-section-header">
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <h2 className="teams-section-title">Add New Hiring Managers</h2>
                        <div className="teams-tooltip">
                            <div className="teams-tooltip-icon">?</div>
                            <div className="teams-tooltip-content">
                                Contacts for interview scheduling. Multiple allowed per organization.
                            </div>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={addNewHiringManagerField}
                        className="teams-button teams-button-secondary"
                    >
                        + Add Manager
                    </button>
                </div>

                <div style={{ marginBottom: '20px' }}>
                    {newHiringManagers.map((manager, index) => (
                        <div key={index} className="teams-manager-item">
                            <div className="teams-manager-inputs">
                                <div style={{ flex: 1 }}>
                                    <label htmlFor={`managerName-${index}`} className="teams-label">Name</label>
                                    <input
                                        id={`managerName-${index}`}
                                        type="text"
                                        value={manager.name}
                                        onChange={(e) => updateNewHiringManager(index, 'name', e.target.value)}
                                        placeholder="Enter hiring manager name"
                                        className="teams-input"
                                    />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <label htmlFor={`managerEmail-${index}`} className="teams-label">Email</label>
                                    <input
                                        id={`managerEmail-${index}`}
                                        type="email"
                                        value={manager.email}
                                        onChange={(e) => {
                                            updateNewHiringManager(index, 'email', e.target.value);
                                            // Clear error when user starts typing again
                                            if (addError) {
                                                setAddError('');
                                            }
                                        }}
                                        onBlur={(e) => {
                                            // Validate email on blur if not empty
                                            if (e.target.value.trim()) {
                                                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                                                if (!emailRegex.test(e.target.value.trim())) {
                                                    setAddError('Please enter valid email addresses for all hiring managers');
                                                }
                                            }
                                        }}
                                        placeholder="Enter hiring manager email"
                                        className="teams-input"
                                    />
                                </div>
                            </div>
                            {newHiringManagers.length > 1 && (
                                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px' }}>
                                    <button
                                        type="button"
                                        onClick={() => removeNewHiringManagerField(index)}
                                        className="teams-button teams-button-danger"
                                        style={{ height: '28px', padding: '0 12px' }}
                                    >
                                        Remove
                                    </button>
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                <button
                    type="button"
                    onClick={saveNewHiringManagers}
                    className="teams-button teams-button-success"
                    disabled={addLoading}
                    style={{ width: '100%' }}
                >
                    {addLoading ? 
                      <><span className="teams-spinner"></span>Saving...</> : 
                      'Save Hiring Managers'}
                </button>

                {/* Add New Hiring Managers Messages */}
                {addError && (
                    <div className="teams-message teams-message-error" style={{ marginTop: '16px' }}>
                        {addError}
                    </div>
                )}
                {addSuccess && (
                    <div className="teams-message teams-message-success" style={{ marginTop: '16px' }}>
                        {addSuccess}
                    </div>
                )}
            </div>

            {/* Existing Hiring Managers Section */}
            <div className="teams-card" style={{ marginTop: '20px' }}>
                <div className="teams-section-header">
                    <h2 className="teams-section-title">Existing Hiring Managers</h2>
                </div>

                {fetchLoading && (
                    <div style={{ display: 'flex', alignItems: 'center', padding: '16px 0' }}>
                        <span className="teams-spinner"></span>
                        <span>Loading hiring managers...</span>
                    </div>
                )}

                {fetchError && (
                    <div className="teams-message teams-message-error">
                        {fetchError}
                    </div>
                )}

                {!fetchLoading && hasInitialLoaded && existingHiringManagers.length === 0 && !fetchError && (
                    <div style={{ 
                        textAlign: 'center', 
                        padding: '24px 16px', 
                        color: '#605e5c',
                        fontStyle: 'italic',
                        border: '1px dashed var(--border-color)',
                        borderRadius: '2px',
                        margin: '16px 0'
                    }}>
                        No hiring managers found.
                    </div>
                )}

                {!fetchLoading && existingHiringManagers.length > 0 && (
                    <>
                        {/* Bulk Actions Header */}
                        <div style={{ 
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '8px 12px',
                            backgroundColor: '#f9f9f9',
                            marginBottom: '12px',
                            borderRadius: '2px'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <label style={{ 
                                    display: 'flex', 
                                    alignItems: 'center',
                                    cursor: 'pointer' 
                                }}>
                                    <input
                                        type="checkbox"
                                        checked={isSelectAll}
                                        onChange={handleSelectAll}
                                        style={{ marginRight: '8px' }}
                                    />
                                    <span>
                                        Select All ({existingHiringManagers.length})
                                    </span>
                                </label>
                            </div>
                            
                            {selectedManagers.size > 0 && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <span style={{ 
                                        fontWeight: '600',
                                        color: 'var(--teams-primary)',
                                        fontSize: '14px'
                                    }}>
                                        {selectedManagers.size} selected
                                    </span>
                                    <button
                                        type="button"
                                        onClick={bulkDeleteManagers}
                                        className="teams-button teams-button-danger"
                                        disabled={bulkDeleteLoading}
                                    >
                                        {bulkDeleteLoading ? 
                                          <><span className="teams-spinner"></span>Deleting...</> : 
                                          `Delete Selected (${selectedManagers.size})`}
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Managers List */}
                        <div style={{ 
                            maxHeight: '320px', 
                            overflowY: 'auto',
                            padding: '4px'
                        }}>
                            {existingHiringManagers.map((manager, index) => (
                                <div key={index} style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    padding: '10px 12px',
                                    backgroundColor: selectedManagers.has(manager.email) ? 'rgba(98, 100, 167, 0.05)' : 'transparent',
                                    borderRadius: '2px',
                                    marginBottom: '8px',
                                    border: '1px solid var(--border-color)',
                                    transition: 'all 0.15s ease'
                                }}>
                                    <label style={{ 
                                        display: 'flex', 
                                        alignItems: 'center',
                                        cursor: 'pointer',
                                        marginRight: '12px'
                                    }}>
                                        <input
                                            type="checkbox"
                                            checked={selectedManagers.has(manager.email)}
                                            onChange={() => handleSelectManager(manager.email)}
                                            style={{ marginRight: '8px' }}
                                        />
                                    </label>
                                    
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontWeight: '600' }}>{manager.name}</div>
                                        <div style={{ 
                                            color: 'var(--text-secondary)',
                                            fontSize: '13px',
                                            fontFamily: 'monospace'
                                        }}>{manager.email}</div>
                                    </div>
                                </div>
                            ))}
                            
                            {/* Scroll indicator */}
                            {existingHiringManagers.length > 4 && (
                                <div style={{
                                    textAlign: 'center',
                                    padding: '8px',
                                    color: 'var(--text-secondary)',
                                    fontSize: '12px',
                                    fontStyle: 'italic'
                                }}>
                                    Scroll to see more managers
                                </div>
                            )}
                        </div>
                    </>
                )}

                {deleteError && (
                    <div className="teams-message teams-message-error">
                        {deleteError}
                    </div>
                )}
            </div>
        </div>
    );
};

export default HiringManagersForm;
